import './index.css';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState, cloneElement, useCallback } from 'react';
import classnames from 'classnames';
import { keyboardMatch, keyboardKeys } from '@hh.ru/magritte-common-keyboard';
import { Action } from '@hh.ru/magritte-ui-action';
import { ActionBar } from '@hh.ru/magritte-ui-action-bar';
import { BottomSheetFooter, BottomSheet } from '@hh.ru/magritte-ui-bottom-sheet';
import { Button } from '@hh.ru/magritte-ui-button';
import { CellText, Cell } from '@hh.ru/magritte-ui-cell';
import { ArrowPathClockwiseOutlinedSize24, CrossOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { SearchInput } from '@hh.ru/magritte-ui-input';
import { Loader } from '@hh.ru/magritte-ui-loader';
import { NavigationBar } from '@hh.ru/magritte-ui-navigation-bar';
import { SelectError } from './SelectError.js';
import { SelectOption } from './SelectOption.js';
import { getChecked } from './getChecked.js';
import { isArraysEquals } from './isArraysEquals.js';
import '@hh.ru/magritte-ui-breakpoint';
import '@hh.ru/magritte-ui-icon/variants/icon';
import '@hh.ru/magritte-ui-spacing';
import '@hh.ru/magritte-ui-title';
import '@hh.ru/magritte-ui-typography';
import '@hh.ru/magritte-ui-checkable-card/CheckableCardElement';
import '@hh.ru/magritte-ui-checkbox-radio';

var styles = {"select-bottom-sheet":"magritte-select-bottom-sheet___1RZDJ_10-0-15","selectBottomSheet":"magritte-select-bottom-sheet___1RZDJ_10-0-15","select-bottom-sheet_checkbox":"magritte-select-bottom-sheet_checkbox___hwTgo_10-0-15","selectBottomSheetCheckbox":"magritte-select-bottom-sheet_checkbox___hwTgo_10-0-15","select-bottom-sheet_radio":"magritte-select-bottom-sheet_radio___qvFFV_10-0-15","selectBottomSheetRadio":"magritte-select-bottom-sheet_radio___qvFFV_10-0-15","full-screen-placeholder":"magritte-full-screen-placeholder___zPC8k_10-0-15","fullScreenPlaceholder":"magritte-full-screen-placeholder___zPC8k_10-0-15","error-wrapper":"magritte-error-wrapper___P7jYg_10-0-15","errorWrapper":"magritte-error-wrapper___P7jYg_10-0-15"};

const SelectOptionDefault = ({ data, input, type, }) => {
    if (type === 'delimiter') {
        return (jsx(CellText, { maxLines: 1, style: "secondary", children: data.text }));
    }
    if (type === 'label') {
        return jsx(CellText, { maxLines: 1, children: data.text });
    }
    return (jsx(Cell, { Element: "div", align: "center", right: input, children: jsx(CellText, { maxLines: 1, children: data.text }) }));
};
const RenderButtonsContainerDefault = ({ clearButton, applyChangesButton }) => {
    const primaryActions = [clearButton, applyChangesButton].filter(Boolean);
    return jsx(ActionBar, { type: "mobile", primaryActions: primaryActions });
};
const RenderHeaderDefault = ({ onCloseBottomSheet, searchValue, setSearchValue, searchable, headerTitle, onSearchSubmit, optionsCount, onCloseBottomSheetWithUpdateValues, }) => {
    return (jsx(NavigationBar, { title: headerTitle, right: jsx(Action, { icon: CrossOutlinedSize24, onClick: onCloseBottomSheet, "data-qa": "select-bottom-sheet-navigation-close" }), options: searchable && (jsx(SearchInput, { clearable: true, size: "medium", value: searchValue, onChange: setSearchValue, onKeyDown: (event) => {
                if (onSearchSubmit && keyboardMatch(event.nativeEvent, keyboardKeys.Enter)) {
                    onSearchSubmit(event, onCloseBottomSheet, optionsCount, onCloseBottomSheetWithUpdateValues);
                }
            }, "data-qa": "bottom-sheet-navigation-input", autoComplete: "off" })) }));
};
const SelectBottomSheet = ({ type, name, multiple, clearButton: _clearButton, applyChangesButton: _applyChangesButton, useInitialValuesOnClearAction, loading, error, renderHeader = RenderHeaderDefault, renderItem = SelectOptionDefault, renderButtonsContainer = RenderButtonsContainerDefault, renderAlertOnExitWithoutSaving, headerTitle, onClose, visible, optionList, optionsListRef, searchValue, setSearchValue, searchable, value: values, onChange, clearSelectedValues, setInitialValues, isLimitExceeded, initialValuesOnOpening, setSelectedValues, onBottomSheetOpen, onBottomSheetClose, onSearchSubmit, onApply, onClear, emptyOptionsHint, emptyOptionsSearchHint, searchInProgress, internalError, reload, innerErrorTrls, optionDataQa, ...props }) => {
    const [alertVisible, setAlertVisible] = useState(false);
    const onCloseAlert = () => setAlertVisible(false);
    const showAlertOnExitWithoutSaving = Boolean(renderAlertOnExitWithoutSaving);
    const showErrorPlaceholder = Boolean(error);
    const onCloseWithAlertCheck = () => {
        if (showAlertOnExitWithoutSaving && multiple) {
            const haveUnsavingChanges = !isArraysEquals(initialValuesOnOpening.current?.map((item) => item.value) ?? [], 
            // eslint-disable-next-line no-nested-ternary
            Array.isArray(values) ? values.map((item) => item.value) : values ? [values.value] : []);
            if (haveUnsavingChanges) {
                setAlertVisible(true);
            }
            else {
                onClose();
            }
        }
        else {
            onClose();
        }
    };
    const clearButton = _clearButton &&
        cloneElement(_clearButton, {
            key: 'clearButton',
            size: 'medium',
            'data-qa': 'magritte-select-clear-action-on-xs',
            onClick: () => {
                if (useInitialValuesOnClearAction) {
                    setInitialValues();
                }
                else {
                    clearSelectedValues();
                }
                onClose();
                onClear?.();
            },
        });
    const needAddChangesButton = multiple && _applyChangesButton;
    const applyChangesButton = (needAddChangesButton &&
        cloneElement(_applyChangesButton, {
            key: 'applyChangesButton',
            size: 'medium',
            'data-qa': 'magritte-select-apply-on-xs',
            onClick: () => {
                setSelectedValues();
                onClose();
                onApply?.({ isMobile: true, searchValue });
            },
        })) ||
        undefined;
    const handleChange = useCallback((...args) => {
        if (!multiple) {
            onClose();
        }
        onChange(...args);
    }, [multiple, onChange, onClose]);
    const haveClearOrApplyChangesButtons = clearButton || applyChangesButton;
    const footer = !showErrorPlaceholder ? (!loading &&
        haveClearOrApplyChangesButtons &&
        renderButtonsContainer({
            clearButton,
            applyChangesButton,
        })) : (jsx(BottomSheetFooter, { children: error && error.resetButton }));
    const emptyOptionsHintVisible = optionList.length === 0 &&
        !searchValue &&
        !!emptyOptionsHint &&
        !loading &&
        !showErrorPlaceholder &&
        !searchInProgress &&
        !internalError;
    const emptyOptionsSearchHintVisible = optionList.length === 0 &&
        searchValue &&
        !!emptyOptionsSearchHint &&
        !loading &&
        !showErrorPlaceholder &&
        !searchInProgress &&
        !internalError;
    const onCloseBottomSheetWithUpdateValues = () => {
        setSelectedValues();
        onCloseWithAlertCheck();
    };
    const optionsVisible = !loading &&
        !emptyOptionsSearchHintVisible &&
        !emptyOptionsHintVisible &&
        !internalError &&
        !showErrorPlaceholder;
    return (jsxs(Fragment, { children: [jsxs(BottomSheet, { ...props, ref: optionsListRef, allowScrollWhileFocused: true, keyboardOverlaysFooter: false, visible: visible, showDivider: false, withContentPaddings: showErrorPlaceholder, header: renderHeader({
                    onCloseBottomSheet: onCloseWithAlertCheck,
                    searchValue,
                    setSearchValue,
                    searchable,
                    headerTitle,
                    onSearchSubmit,
                    optionsCount: optionList.length,
                    onCloseBottomSheetWithUpdateValues,
                }), onClose: onCloseWithAlertCheck, footer: internalError && innerErrorTrls ? (jsx(BottomSheetFooter, { children: jsx(Button, { mode: "primary", style: "accent", size: "medium", onClick: reload, stretched: true, icon: jsx(ArrowPathClockwiseOutlinedSize24, {}), children: innerErrorTrls.reloadButton }) })) : (footer), onAppear: onBottomSheetOpen, onAfterExit: onBottomSheetClose, children: [internalError && innerErrorTrls && jsx(SelectError, { innerErrorTrls: innerErrorTrls, reload: reload }), emptyOptionsSearchHintVisible && (jsx("div", { className: styles.fullScreenPlaceholder, children: emptyOptionsSearchHint })), emptyOptionsHintVisible && jsx("div", { className: styles.fullScreenPlaceholder, children: emptyOptionsHint }), loading && !showErrorPlaceholder && (jsx("div", { className: styles.fullScreenPlaceholder, children: jsx(Loader, { size: 24 }) })), showErrorPlaceholder && jsx("div", { className: styles.errorWrapper, children: error && error.placeholder }), optionsVisible && (jsx("div", { className: classnames(styles.selectBottomSheet, styles[`select-bottom-sheet_${type}`]), "data-qa": "magritte-select-option-list-on-xs", children: optionList.map(({ disabled: _disabled, type: optionType, ...option }) => {
                            const checked = Boolean(option.data && getChecked(option.data, values ?? []));
                            const disabled = !checked && (_disabled || isLimitExceeded);
                            return (jsx(SelectOption, { name: name, checked: checked, onChange: handleChange, render: renderItem, disabled: disabled, type: optionType || type, plain: type === 'label', multiple: multiple, "data-qa": optionDataQa, isXS: true, ...option }, option.data.value));
                        }) }))] }), renderAlertOnExitWithoutSaving?.({
                alertVisible,
                onCloseAlert,
                onCloseBottomSheetAndAlert: () => {
                    onCloseAlert();
                    onClose();
                },
            })] }));
};

export { SelectBottomSheet };
//# sourceMappingURL=SelectBottomSheet.js.map
